/*jshint esversion: 6 */

/*! webmd.url */

/**
 * @namespace Functions for URL manipulation.
 *
 * <p>These functions must support partial URLs that contain only a query string, such as "?a=1&b=2".</p>
 */

export let getParam;
export let getParams;
export let setParam;
export let appendParam;
export let deleteParam;
export let setHash;
export let getHash;
export let clear;
export let getHostname;
export let getLifecycle;
export let getEnv;
export let addLifecycleAndEnv;
export let getSLD;
let _getParts;
let _splitParams;
let _buildUrl;
let _getPartsRegExp;

if (typeof window !== 'undefined') {

  /**
   * Get a single parameter value from a URL
   *
   * @returns {String|undefined} Value of the URL parameter, or undefined if the parameter is not found.
   * If the URL contains multiple paramaters using the same name, this function returns the first one.
   * If you need to retrieve all the parameters using the same name, see {@link webmd.url.getParams}.
   *
   * <p>Since the return value might be undefined, you should always verify the value before
   * attempting to use it. A possible method is to use the logical or operator to set a default
   * value. Refer to the examples for more information.</p>
   *
   * @param {String} param Name of the parameter to get from the URL.
   * @param {String} [url=location.href] URL to search for the parameter.
   *
   * @example
   * // If current page is 'http://example.com/?a=5&b=1&a=3'
   * a = webmd.url.getParam('a') || '';
   * // a is '5'
   *
   * @example
   * var a = webmd.url.getParam('a', 'http://example.com/?a=5&b=1&a=3') || '';
   * // a is '5'
   *
   * @example
   * // If current page is 'http://example.com/'
   * a = webmd.url.getParam('a');
   * // a is undefined
   *
   * @example
   * // INCORRECT example
   * // If current page is 'http://example.com/'
   * name = webmd.url.getParam('name');
   * alert('Hello, ' + name);
   * // This will alert 'Hello, undefined', which is probably not what you wanted
   *
   * @example
   * // If current page is 'http://example.com/'
   * name = webmd.url.getParam('name') || 'user';
   * alert('Hello, ' + name);
   * // This will alert 'Hello, user' since name is not a parameter in the URL
   *
   * @example
   * // If current page is 'http://example.com/?name=Fred'
   * name = webmd.url.getParam('name') || 'user';
   * alert('Hello, ' + name);
   * // This will alert 'Hello, Fred' since name is a parameter in the URL
   *
   * @see webmd.url.getParams
   */
  getParam = function(param, url) {

    let p;

    // Get object of all url parameters
    p = getParams(url);

    // Return undefined if param is not in the URL
    if (!p[param]) {
      return undefined;
    }

    // Return the first value found for the param
    return p[param][0];
  };

  /**
   * Get an object containing all the URL parameters.
   *
   * @returns {Object} An object of key:value pairs where the key is the name of the parameter,
   * and the value is an array of values for that parameter.
   *
   * <p>Note: before accessing a parameter, you should always check that the parameter is defined
   * in the returned object. If the parameter is undefined and you attempt to use an array index
   * it will cause an exception.</p>
   *
   * @param {String} [url=location.href] URL to search for the parameter. Default
   * is the current page URL.
   *
   * @example
   * // If current page is 'http://example.com?fruit=apple&fruit=pear&q=5'
   * params = webmd.url.getParams();
   * if (params.fruit) {
   *   fruits = 'Fruits: ' + params.fruit.join(', ');
   * } else {
   *   fruits = 'No fruits.';
   * }
   * // Result: "Fruits: apple, pear'
   *
   * @example
   * // Get the value of the first instance of the "fruit" parmeter
   * params = webmd.url.getParams();
   * fruit = params.fruit ? params.fruit[0] : '';
   *
   * @example
   * // INCORRECT - this will cause an exception if the "fruit" parameter is undefined
   * fruit = webmd.url.getParams().fruit[0];
   *
   * @see webmd.url.getParam
   */
  getParams = function(url) {

    if (url === undefined) {
      url = location.href;
    }

    return _getParts(url).params;
  };

  /**
   * Replace a paramter within a URL, or add a new parameter to the URL.
   * This function assumes you want only a single use of the parameter in the URL,
   * so it first removes all occurrences of that parameter, then adds back a single instance.
   * If you need multiple occurances of the same paramter use {@link webmd.url.appendParam}.
   *
   * <p>Note: the new parameter is not necessariliy added to the end of the URL.
   * The order of the URL parameters might not be maintained.</p>
   *
   * @returns {String} The new URL with the parameter added.
   *
   * @param {String} param Name of the parameter to add or replace in the URL.
   * @param {String} value Value for the parameter.
   * @param {String} [url=location.href] URL to add the parameter.
   *
   * @example
   * url = webmd.url.setParam('a', 1);
   *
   * @example
   * url = setParam('a', 1, url);
   *
   * @see webmd.url.appendParam
   */
  setParam = function(param, value, url) {

    // First delete the param if it is already in the URL
    url = deleteParam(param, url);

    // Then append the param to the URL
    return appendParam(param, value, url);
  };

  /**
   * Append a parameter value to a URL. This function assumes the URL can contain
   * multiple instances of the same parameter name, so if the URL already contains
   * that parameter, an additional parameter using the same name will be added.
   *
   * <p>Note: the new parameter is not necessariliy added to the end of the URL.
   * The order of the URL parameters might not be maintained.</p>
   *
   * @returns {String} The new URL with the parameter added.
   *
   * @param {String} param Name of the parameter to add to the URL.
   * @param {String} value Value for the parameter.
   * @param {String} [url=location.href] URL to add the parameter.
   *
   * @example
   * url = webmd.url.appendParam('a', 1);
   *
   * @example
   * url = webmd.url.appendParam('a', 1, url);
   *
   * @see webmd.url.setParam
   */
  appendParam = function(param, value, url) {

    let parts;

    // If a url was not specified, use the current page url
    if (url === undefined) {
      url = location.href;
    }

    // Split the url into components
    parts = _getParts(url);

    // Just in case value was not specified, prevent the word "undefined' from appearing in the url
    if (value === undefined) {
      value = "";
    }

    // If the param does not already exist, create it with an array to hold values
    if (!parts.params[param]) {
      parts.params[param] = [];
    }

    // Add the new parameter to the end of the array of parameters
    parts.params[param].push(value);

    // Construct the new URL
    return _buildUrl(parts);
  };

  /**
   * Remove a parameter from a URL
   *
   * <p>Note: if the URL contains multiple parameters of the same name, this
   * function will remove all matching parameters.</p>
   *
   * @returns {String} New URL after parameter has been removed.
   *
   * @param {String} param Name of the parameter to remove from the URL.
   * @param {String} [url=location.href] URL to search for the parameter.
   *
   * @example
   * url = webmd.url.deleteParam('a');
   *
   * @example
   * url = webmd.url.deleteParam('a', url);
   *
   * @see webmd.url.clear
   */
  deleteParam = function (param, url) {

    let parts;

    if (url === undefined) {
      url = location.href;
    }

    // Split the url into its components
    parts = _getParts(url);

    // Delete the parameter from the params object
    delete parts.params[param];

    // Re-construct the URL
    return _buildUrl(parts);
  };

  /**
   * Get the hash (document fragment) from a URL
   *
   * @returns {String} Hash part of the URL (without the '#' character), or empty string.
   *
   * @param {String} [url=document.location.href] URL to search for the hash.
   *
   * @example
   * hash = webmd.url.getHash();
   *
   * @example
   * hash = webmd.url.getHash(url);
   *
   * @see webmd.url.setHash
   */
  getHash = function(url) {

    if (url === undefined) {
      url = location.href;
    }

    // I can has the hash, but do not encode it
    return _getParts(url).hash;
  };


  /**
   * Set or change the hash (document fragment) on a URL.
   *
   * The hash value will be URL encoded before being added to the URL.
   *
   * @returns {String} The new URL
   *
   * @param {String} hash New value for the hash.
   * @param {String} [url=location.href] URL to add the hash.
   *
   * @example
   * url = webmd.url.setHash('myvalue')
   *
   * @example
   * url = webmd.url.setHash('myvalue', url)
   *
   * @see webmd.url.getHash
   * @see webmd.url.clear
   */
  setHash = function(hash, url) {

    if (url === undefined) {
      url = location.href;
    }

    // Remove any existing hash
    url = url.replace(/#.*/, '');

    // In case a developer specified a hash starting with a # character, remove it
    hash = hash.replace(/^#/, '');

    // return the url with hash added but do not encode it
    if (hash) {
      url = url + '#' + hash;
    }

    return url;
  };

  /**
   * Clear the query string and hash from the URL
   *
   * @returns {String} The new URL
   *
   * @param {String} [url=location.href] URL to search for the hash.
   *
   * @example
   * url = webmd.url.clear()
   *
   * @example
   * url = webmd.url.clear(url)
   */
  clear = function(url) {

    if (url === undefined) {
      url = location.href;
    }

    return _getParts(url).url;
  };

  /**
   * Get the hostname part of a URL.
   *
   * @returns {String} The hostname of the URL.
   *
   * @param {String} [url=location.href] URL to search for the hostname.
   */
  getHostname = function(url) {

    let a;

    // If url was not supplied, use the current location
    if (url === undefined) {
      return location.hostname;
    }

    // If a url was supplied, get the hostname part
    // Easiest way is to create an A element and use browser native hostname parameter
    a = document.createElement("a");
    a.href = url;
    return a.hostname;
  };

  /**
   * Determine the lifecycle (live, preview, staging) of a URL
   *
   * @returns {String} The lifecycle with a leading period, or a blank string for live.
   * For example:
   * <ul>
   * <li>"" = live</li>
   * <li>".preview"</li>
   * <li>".staging"</li>
   * </ul>
   *
   * @param {String} [url=location.href] The URL to use in determining the environment.
   * If not provided, will use document.location.href.
   *
   * @example
   * lc = webmd.url.getLifecycle('http://www.preview.webmd.com');
   * // Returns '.preview'
   *
   * @example
   * lc = webmd.url.getLifecycle('http://www.webmd.com');
   * // Returns ''
   */
  getLifecycle = function(url) {

    let hostname;

    hostname = getHostname(url);

    // Return the lifecycle, or empty string for live
    if (hostname.indexOf('.preview.') !== -1) {
      return '.preview';
    }

    if (hostname.indexOf('.staging.') !== -1) {
      return '.staging';
    }

    return '';
  };

  /**
   * Determine the environment (production, qa00, perf, etc.)
   *
   * <p>The environment will be determined using one of the following paterns:</p>
   * <ul>
   * <li> {subdomain}.{lifecycle}.{env}.{domain}</li>
   * <li> {subdomain}.{lifecycle}.{env}.m.{domain}</li>
   * </ul>
   *
   * <p>Where {lifecycle} is optional and can contain "preview" or "staging",
   * and {env} is optional.</p>
   *
   * @returns {String} The part of the URL that represents the environment,
   * with a leading period. For example:
   * <ul>
   * <li>"" = production (or unknown)</li>
   * <li>".perf" = perf</li>
   * <li>".qa00" = qa00</li>
   * </ul>
   *
   * @param {String} [url=location.href] The URL to use in determining the environment.
   * If not provided, will use document.location.href.
   *
   * @example
   * env = webmd.url.getEnv('http://www.perf.webmd.com');
   * // Returns '.perf'
   *
   * @example
   * env = webmd.url.getEnv('http://www.webmd.com');
   * // Returns ''
   */
  getEnv = function(url) {

    let hostname, parts, env;

    hostname = getHostname(url);

    // Split the hostname into parts
    parts = hostname.split('.');

    // Remove the last two parts
    parts.pop(); // com
    parts.pop(); // webmd

    // Get the next part.
    // This could be the environment (qa01), or "m" if we are looking at a mobile URL,
    // or the subdomain (www)
    env = parts.pop();

    // If we got "m", then skip it and use the next part,
    // to account for mobile URLs like www.{env}.m.webmd.com
    if (env === 'm') {
      env = parts.pop();
    }

    // If we got "preview" or "staging", then assume we're in production.
    if (env === 'preview' || env === 'staging') {
      env = '';
    }

    // If there are no additional parts to the hostname, then we must assume we are in production
    // because we reached the subdomain (www|men|diabetes|etc).
    // In a non-production environment there will always be another like "www.qa01"
    if (env && parts.length) {

      // Add a dot in front of the environment so it can easily be added to another url
      // 'http://www' + webmd.url.getEnv() + '.webmd.com
      env = '.' + env;

    } else {
      env = '';
    }

    return env;
  };

  /**
   * Modify a production URL and change it to a different environment (production, qa00, perf, etc.)
   *
   * <p>Note: this should only be used on production URLs - if the URL provided already contains an env or lifecyle,
   * then the final URL will not be correct.</p>
   *
   * @returns {String} The modified URL set to the correct environment.
   *
   * @param {String} url The URL to modify. This should be a production URL without an environment.
   * @param {String} [baseUrl=location.href] The URL to use in determining the environment.
   * If not provided, will use location.href.
   *
   * @example
   * url = webmd.url.addLifecycleAndEnv('http://www.webmd.com/');
   * // If running on a page in http://www.qa01.webmd.com/:
   * // Results in 'http://www.qa01.webmd.com/'
   *
   * @example
   * url = webmd.url.addLifecycleAndEnv('http://www.m.webmd.com/some/page/');
   * // If running on a page in http://www.qa01.webmd.com/:
   * // Results in 'http://www.qa01.m.webmd.com/some/page/'
   *
   * @example
   * url = webmd.url.addLifecycleAndEnv('http://www.webmd.com/', 'http://www.qa01.webmd.com');
   * // results in: http://www.qa01.webmd.com
   *
   * @example
   * url = webmd.url.addLifecycleAndEnv('http://www.webmd.com/', 'http://www.preview.qa01.webmd.com');
   * // results in: http://www.preview.qa01.webmd.com
   *
   * @example
   * url = webmd.url.addLifecycleAndEnv('http://www.webmd.com/', 'http://www.preview.qa01.m.webmd.com');
   * // results in: http://www.preview.qa01.webmd.com
   *
   * @example
   * url = webmd.url.addLifecycleAndEnv('http://www.m.webmd.com/', 'http://www.qa01.webmd.com/');
   * // results in: http://www.qa01.m.webmd.com/
   */
  addLifecycleAndEnv = function(url, baseUrl) {

    // Insert the lifecycle and environment before m.webmd.com or before webmd.com
    // Note we are using a "lazy" regexp (+?) as opposed to greedy,
    // to ensure we don't grab the ".m" part of the url until later
    return url.replace(/(\/\/[^\/]+?)(\.m)?(\.\w+\.\w+)/,
               '$1' + getLifecycle(baseUrl) + getEnv(baseUrl) + '$2$3');
  };

  /**
   * Return the "second level domain" of a URL. For example, "webmd.com".
   *
   * <p>This can be useful when you need to create a cookie and want it to apply across the domain
   * and all subdomains.</p>
   *
   * @example
   * webmd.cookie.set('mycookie', 1, {domain: webmd.url.getSLD()});
   */
  getSLD = function(url) {

    // Get the hostname from the URL, turn it into an array,
    // then get the last two parts of the array and turn it back
    // into a string.
    return getHostname(url).split('.').splice(-2, 2).join('.');
  };

  //--------------------------------------------------
  // Private functions
  // These are not intended to be used outside of this object.
  //

  /**
   * @private
   * @description
   * Regular expression used to split a URL into parts.
   * To improve performance, this is created once instead of creating it each
   * time a URL is processed.
   */
  _getPartsRegExp = new RegExp('([^#?]*)\\??([^#]*)?#?(.*)?');


  /**
   * @private
   * @description
   * Splits a URL into its component parts.
   *
   * @returns
   * {Object} An object with the following string parameters:
   * o.url = anything before the question mark
   * o.query = anything between the question mark and hash (#)
   * o.hash = anything after the hash
   * o.params = array of objects with 'name' and 'value' parameters.
   *
   * @param {String} [url=document.location.href] URL to search for the hash.
   */
  _getParts = function (url) {

    let matches, result;

    // Create the default result object
    result = {
      url:"",
      query:"",
      hash:"",
      params:{}
    };

    // Get the matches for the RegExp
    matches = _getPartsRegExp.exec(url);

    // Make sure we got some matches from the RegExp
    if (matches !== null) {

      // Check if we actually got a match for the parts
      result.url    = matches[1] === undefined ? "" : matches[1];
      result.query  = matches[2] === undefined ? "" : matches[2];
      result.hash   = matches[3] === undefined ? "" : decodeURIComponent(matches[3]);

      // Turn the query string into an object
      result.params = _splitParams(result.query);
    }

    return result;
  };

  /**
   * @private
   * @description
   * Splits a query string (a=1&b=2) into an object that contains name:value for each parameter.
   * The value of each parameter is always an array of values (that is, if there is only one value
   * it will still be stored as an array containing a single item).
   *
   * Decodes the names & values for special (\nnnn) values.
   *
   * @returns {Object} An object with name:value pairs for each parameter.
   *
   * @param {String} params The search query part of the URL (without the ?) such as (a=1&b=2)
   */
  _splitParams = function(paramsString) {

    let i, paramsArray, nameValueArray, valueArray, name, value, result;

    result = {};

    if (paramsString) {

      // Convert all '+' characters to spaces,
      // then split into individual key=value pairs
      paramsArray = paramsString.replace(/\+/g, ' ').split('&');

      for (i=0; i<paramsArray.length; i++) {

        nameValueArray = paramsArray[i].split('=');

        name = nameValueArray[0];
        value = nameValueArray[1] || '';

        if (name) {

          // Decode the name and values
          name = decodeURIComponent(name);
          value = decodeURIComponent(value);

          // Create an array for the value(s)
          // or if we already encountered this parameter name, use the array that was previously created
          valueArray = result[name] || [];

          // Add the value to the array
          valueArray.push(value);

          // Now save the values array in the result object for this parameter name
          result[name] = valueArray;
        }
      }
    }

    return result;
  };

  /**
   * @private
   * @description
   * Reconstructs a URL based on the parts object that was created by {@link webmd.url._getParts}
   *
   * @returns {String} The new URL
   *
   * @param {Object} parts An object of URL parts as returned by {@link webmd.url._getParts}
   */
  _buildUrl = function(parts) {

    let i, paramName, params, result, separator, values;

    // Shortcut variable
    params = parts.params;

    // Start with the url part
    result = parts.url;

    // Add "?" in front of the first parameter,
    // then "&" in front of subsequent parameters
    separator = "?";

    // Loop through the params object
    for (paramName in params) {

      // Make sure we loop through only the parameters of this object
      // (not any that might have inherited through the prototype)
      if (params.hasOwnProperty(paramName)) {

        values = params[paramName];

        // Loop through the values for this parameter and add each to the result
        for (i = 0; i < values.length; i++) {

          // On the first parameter, separator will be "?"
          result += separator + encodeURIComponent(paramName) + '=' + encodeURIComponent(values[i]);
      
          // On subsequent loops use ampersand as a separator
          separator = '&';
        }
      }

    }
    
    // Add the hash back to the URL if it was present
    // removing endcoding from the hash, as it doesn't need to be encoded
    if (parts.hash) {
      result += '#' + parts.hash;
    }

    return result;
  };
}